import React, { useMemo, useState } from "react";
import {
  Users,
  Star,
  UserCog,
  Award,
  Shield,
  Plus,
  Loader2,
  Mail,
  CheckCircle2,
  AlertCircle,
  UserPlus,
  RefreshCw,
} from "lucide-react";

import { UserRole } from "../../app/api/authApi";
import { useAppSelector } from "../../app/hooks";
import {
  useDeactivateUserMutation,
  useGetUsersQuery,
  useInviteAdminMutation,
  useUpdateUserMutation,
} from "../../app/api/adminApi";
import { useGetNominationsQuery } from "../../app/api/fellowsApi";
import AdminAllMembers from "./AdminAllMembers";
import AdminLegacyFellows from "./AdminLegacyFellows";
import AdminInnerCircle from "./AdminInnerCircle";
import AdminRegionalReps from "./AdminRegionalReps";
import { CAN_VIEW_FELLOWS, CAN_VIEW_IC, CAN_VIEW_REPS } from "./MemberShared";

/* ─────────────────────────────────────────────────────────────────
   TAB DEFINITION
───────────────────────────────────────────────────────────────── */
type TabKey = "all" | "fellows" | "inner_circle" | "reps" | "admins";

interface TabDef {
  key: TabKey;
  label: string;
  icon: React.ReactNode;
  visible: (role: UserRole) => boolean;
  badge?: number;
}

/* ─────────────────────────────────────────────────────────────────
   ROLE DESCRIPTIONS
───────────────────────────────────────────────────────────────── */
const ROLE_DESCRIPTION: Partial<Record<UserRole, string>> = {
  admin:
    "Manage all members, fellows, regional reps, and administrative accounts.",
  committee_chair: "Manage members, approve fellows, and view Inner Circle.",
  committee_member: "View member information.",
  portal_manager: "View members and Inner Circle progress.",
  regional_rep: "Submit nominations and view your region's members.",
};

const ADMIN_ROLE_OPTIONS: UserRole[] = [
  "admin",
  "committee_chair",
  "committee_member",
  "portal_manager",
  "regional_rep",
  "co",
  "cyp",
  "ryp",
  "mentor",
];

const ADMIN_ROLE_LABEL: Record<UserRole, string> = {
  admin: "Admin",
  committee_chair: "Committee Chair",
  committee_member: "Committee Member",
  portal_manager: "Portal Manager",
  regional_rep: "Regional Rep",
  mentee: "Mentee",
  co: "Continental Overseer",
  cyp: "Continental Youth Pastor",
  ryp: "Regional Youth Pastor",
  mentor: "Mentor",
};

const ROLE_CHIP: Record<string, string> = {
  admin: "bg-red-50 text-red-700 ring-red-200",
  committee_chair: "bg-purple-50 text-purple-700 ring-purple-200",
  committee_member: "bg-blue-50 text-blue-700 ring-blue-200",
  portal_manager: "bg-amber-50 text-amber-700 ring-amber-200",
  regional_rep: "bg-green-50 text-green-700 ring-green-200",
  co: "bg-gold-50 text-gold-700 ring-gold-200",
  cyp: "bg-teal-50 text-teal-700 ring-teal-200",
  ryp: "bg-orange-50 text-orange-700 ring-orange-200",
};

// Password field removed — auto-generated on the backend
// Region and ministryRole removed — not required at invite time
const EMPTY_INVITE = {
  firstName: "",
  lastName: "",
  email: "",
  role: "committee_member" as UserRole,
  managedRegion: "",
};

/* ─────────────────────────────────────────────────────────────────
   SMALL UI HELPERS
───────────────────────────────────────────────────────────────── */

const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

const InlineMessage: React.FC<{
  kind: "success" | "error";
  children: React.ReactNode;
}> = ({ kind, children }) => (
  <div
    className={`flex items-center gap-2 text-xs rounded-xl px-4 py-3 border ${
      kind === "success"
        ? "text-green-700 bg-green-50 border-green-100"
        : "text-red-600 bg-red-50 border-red-100"
    }`}
  >
    {kind === "success" ? (
      <CheckCircle2 size={13} className="shrink-0" />
    ) : (
      <AlertCircle size={13} className="shrink-0" />
    )}
    {children}
  </div>
);

const RolePill: React.FC<{ role: UserRole }> = ({ role }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ring-1 ${
      ROLE_CHIP[role] ?? "bg-gray-50 text-gray-700 ring-gray-200"
    }`}
  >
    {ADMIN_ROLE_LABEL[role]}
  </span>
);

/* ─────────────────────────────────────────────────────────────────
   ADMIN ACCOUNTS PANEL (SUPER ADMIN ONLY)
───────────────────────────────────────────────────────────────── */

const AdminAccountsPanel: React.FC = () => {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState(EMPTY_INVITE);
  const [inviteMsg, setInviteMsg] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);

  const {
    data: adminsRes,
    isLoading,
    isFetching,
    refetch,
  } = useGetUsersQuery({
    limit: 100,
    page: 1,
  });

  const [inviteAdmin, { isLoading: inviting }] = useInviteAdminMutation();
  const [updateUser, { isLoading: updating }] = useUpdateUserMutation();
  const [deactivateUser, { isLoading: deactivating }] =
    useDeactivateUserMutation();

  const adminUsers = useMemo(() => {
    const all = adminsRes?.data ?? [];
    return all.filter((u) =>
      [
        "admin",
        "committee_chair",
        "committee_member",
        "portal_manager",
        "regional_rep",
        "co",
        "cyp",
        "ryp",
      ].includes(u.role),
    );
  }, [adminsRes]);

  const handleInviteChange = (
    field: keyof typeof EMPTY_INVITE,
    value: string,
  ) => {
    setInviteForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleInvite = async () => {
    setInviteMsg(null);

    if (
      !inviteForm.firstName.trim() ||
      !inviteForm.lastName.trim() ||
      !inviteForm.email.trim()
    ) {
      setInviteMsg({
        kind: "error",
        text: "First name, last name, and email are required.",
      });
      return;
    }

    try {
      await inviteAdmin({
        firstName: inviteForm.firstName.trim(),
        lastName: inviteForm.lastName.trim(),
        email: inviteForm.email.trim(),
        role: inviteForm.role,
        managedRegion:
          inviteForm.role === "regional_rep"
            ? inviteForm.managedRegion.trim() || undefined
            : undefined,
      }).unwrap();

      setInviteMsg({
        kind: "success",
        text: `${ADMIN_ROLE_LABEL[inviteForm.role]} account created. Login credentials sent to ${inviteForm.email.trim()}.`,
      });
      setInviteForm(EMPTY_INVITE);
      setInviteOpen(false);
      refetch();
    } catch (err: any) {
      setInviteMsg({
        kind: "error",
        text: err?.data?.error || "Could not create admin account.",
      });
    }
  };

  const handleRoleChange = async (userId: string, role: UserRole) => {
    try {
      await updateUser({ id: userId, role }).unwrap();
      refetch();
    } catch {
      // keep quiet in table; failure is obvious if role doesn't update
    }
  };

  const handleDeactivate = async (userId: string) => {
    try {
      await deactivateUser(userId).unwrap();
      refetch();
    } catch {
      // ignore inline
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Administrative accounts
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Create admin-facing accounts and manage their roles and access.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
            >
              {isFetching ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} />
              )}
              Refresh
            </button>

            <button
              onClick={() => setInviteOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-2 text-xs font-semibold text-white hover:bg-purple-700 transition-colors"
            >
              <Plus size={12} />
              Add admin
            </button>
          </div>
        </div>

        {inviteMsg && (
          <div className="mt-4">
            <InlineMessage kind={inviteMsg.kind}>
              {inviteMsg.text}
            </InlineMessage>
          </div>
        )}

        {inviteOpen && (
          <div className="mt-5 rounded-2xl border border-purple-100 bg-purple-50/50 p-4 space-y-4">
            <div className="flex items-center gap-2">
              <UserPlus size={15} className="text-purple-600" />
              <p className="text-sm font-semibold text-gray-900">
                Create administrative account
              </p>
            </div>

            {/* Info notice — password is auto-generated */}
            <div className="flex items-start gap-2 rounded-xl bg-blue-50 border border-blue-100 px-4 py-3">
              <Mail size={13} className="text-blue-500 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-700">
                A secure temporary password will be auto-generated and sent to
                the provided email address along with login instructions.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                value={inviteForm.firstName}
                onChange={(e) =>
                  handleInviteChange("firstName", e.target.value)
                }
                placeholder="First name"
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              />
              <input
                value={inviteForm.lastName}
                onChange={(e) => handleInviteChange("lastName", e.target.value)}
                placeholder="Last name"
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              />
              <input
                value={inviteForm.email}
                onChange={(e) => handleInviteChange("email", e.target.value)}
                placeholder="Email address"
                type="email"
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              />
              <select
                value={inviteForm.role}
                onChange={(e) =>
                  handleInviteChange("role", e.target.value as UserRole)
                }
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              >
                {ADMIN_ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>
                    {ADMIN_ROLE_LABEL[role]}
                  </option>
                ))}
              </select>

              {/* Managed region — only shown for regional_rep */}
              {inviteForm.role === "regional_rep" && (
                <input
                  value={inviteForm.managedRegion}
                  onChange={(e) =>
                    handleInviteChange("managedRegion", e.target.value)
                  }
                  placeholder="Managed region"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 md:col-span-2"
                />
              )}
            </div>

            <div className="flex items-center justify-end gap-2 flex-wrap">
              <button
                onClick={() => {
                  setInviteOpen(false);
                  setInviteForm(EMPTY_INVITE);
                  setInviteMsg(null);
                }}
                className="rounded-lg border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                disabled={inviting}
                className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-xs font-semibold text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {inviting ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Mail size={12} />
                )}
                Send invite
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">
            Existing admin accounts
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Change roles or deactivate accounts. Only the super admin can do
            this.
          </p>
        </div>

        {isLoading ? (
          <div className="p-5 space-y-3">
            {[0, 1, 2].map((i) => (
              <Bone key={i} className="h-16" />
            ))}
          </div>
        ) : adminUsers.length === 0 ? (
          <div className="p-8 text-sm text-gray-400 text-center">
            No administrative accounts found.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {adminUsers.map((admin) => (
              <div
                key={admin.id}
                className="px-5 py-4 flex flex-col lg:flex-row lg:items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-gray-900">
                      {admin.firstName} {admin.lastName}
                    </p>
                    <RolePill role={admin.role} />
                    {!admin.isActive && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-600">
                        Deactivated
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{admin.email}</p>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-400 flex-wrap">
                    {admin.region && <span>Region: {admin.region}</span>}
                    {admin.managedRegion && (
                      <span>Managed: {admin.managedRegion}</span>
                    )}
                    {admin.ministryRole && (
                      <span>Role: {admin.ministryRole}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={admin.role}
                    onChange={(e) =>
                      handleRoleChange(admin.id, e.target.value as UserRole)
                    }
                    disabled={updating || !admin.isActive}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-300"
                  >
                    {ADMIN_ROLE_OPTIONS.map((role) => (
                      <option key={role} value={role}>
                        {ADMIN_ROLE_LABEL[role]}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={() => handleDeactivate(admin.id)}
                    disabled={deactivating || !admin.isActive}
                    className="rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                  >
                    Deactivate
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────── */
const AdminMembers: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user)!;
  const role = (user.role ?? "committee_member") as UserRole;

  const defaultTab: TabKey = role === "regional_rep" ? "fellows" : "all";
  const [activeTab, setActiveTab] = useState<TabKey>(defaultTab);

  const { data: allUsersRes } = useGetUsersQuery({ limit: 1 });
  const { data: icRes } = useGetUsersQuery(
    { tier: "inner_circle", limit: 1 },
    { skip: !CAN_VIEW_IC.includes(role) && role !== "portal_manager" },
  );
  const { data: repsRes } = useGetUsersQuery(
    { role: "regional_rep", limit: 1 },
    { skip: !CAN_VIEW_REPS.includes(role) },
  );
  const { data: nominationsRes } = useGetNominationsQuery(
    { status: "nominated" },
    { skip: !CAN_VIEW_FELLOWS.includes(role) },
  );
  const { data: adminsRes } = useGetUsersQuery(
    { limit: 1, role: "admin" },
    { skip: role !== "admin" },
  );

  const totalMembers = allUsersRes?.total ?? 0;
  const totalIC = icRes?.total ?? 0;
  const totalReps = repsRes?.total ?? 0;
  const pendingNomCount = nominationsRes?.count ?? 0;
  const totalAdmins = adminsRes?.total ?? 0;

  const tabs = useMemo<TabDef[]>(
    () => [
      {
        key: "all",
        label: "All Members",
        icon: <Users size={13} />,
        visible: () => true,
        badge: totalMembers > 0 ? totalMembers : undefined,
      },
      {
        key: "fellows",
        label: "Legacy Fellows",
        icon: <Star size={13} />,
        visible: (r) => CAN_VIEW_FELLOWS.includes(r),
        badge: pendingNomCount > 0 ? pendingNomCount : undefined,
      },
      {
        key: "inner_circle",
        label: "Inner Circle",
        icon: <Award size={13} />,
        visible: (r) => CAN_VIEW_IC.includes(r) || r === "portal_manager",
        badge: totalIC > 0 ? totalIC : undefined,
      },
      {
        key: "reps",
        label: "Regional Reps",
        icon: <UserCog size={13} />,
        visible: (r) => CAN_VIEW_REPS.includes(r),
        badge: totalReps > 0 ? totalReps : undefined,
      },
      {
        key: "admins",
        label: "Admin Accounts",
        icon: <Shield size={13} />,
        visible: (r) => r === "admin" || r === "co" || r === "cyp",
        badge: totalAdmins > 0 ? totalAdmins : undefined,
      },
    ],
    [totalMembers, pendingNomCount, totalIC, totalReps, totalAdmins],
  );

  const visibleTabs = tabs.filter((t) => t.visible(role));

  return (
    <div className="space-y-6 pb-12">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-purple-600 mb-1">
          People Management
        </p>
        <h1 className="text-2xl font-bold text-gray-900">Members</h1>
        <p className="mt-1 text-sm text-gray-500">
          {ROLE_DESCRIPTION[role] ?? "View member information."}
        </p>
      </div>

      <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 w-fit flex-wrap">
        {visibleTabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg transition-all
                ${
                  isActive
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
            >
              {tab.icon}
              {tab.label}
              {tab.badge !== undefined && (
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full transition-colors
                  ${
                    isActive
                      ? tab.key === "fellows"
                        ? "bg-amber-100 text-amber-700"
                        : tab.key === "admins"
                          ? "bg-red-100 text-red-700"
                          : "bg-purple-100 text-purple-700"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {tab.badge.toLocaleString()}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div>
        {activeTab === "all" && <AdminAllMembers />}
        {activeTab === "fellows" && <AdminLegacyFellows />}
        {activeTab === "inner_circle" && <AdminInnerCircle />}
        {activeTab === "reps" && <AdminRegionalReps />}
        {activeTab === "admins" &&
          (role === "admin" || role === "co" || role === "cyp") && (
            <AdminAccountsPanel />
          )}
      </div>
    </div>
  );
};

export default AdminMembers;